import { ArrowSquareOutIcon } from "@phosphor-icons/react/dist/csr/ArrowSquareOut";
import { CrosshairIcon } from "@phosphor-icons/react/dist/csr/Crosshair";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { type FC } from "react";
import { Box, Button, Chip, Divider, Drawer, IconButton, Stack, Typography } from "@mui/material";
import type { DataFlowStep } from "../types";
import { NODE_TYPES, getNodeTypeKey } from "../nodeTypes";
import { useNavigator } from "../../../lib/Hooks";

interface NodeInfoPanelProps {
  node: DataFlowStep | null;
  onClose: () => void;
  onReframe: () => void;
  scriptbookId?: string;
}

const InfoRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="body.small.semibold" color="text50.main" component="span">
      {label}:{" "}
    </Typography>
    <Typography variant="body.small.regular" component="span" sx={{ wordBreak: "break-all" }}>
      {value}
    </Typography>
  </Box>
);

function getNodeTypeBadge(node: DataFlowStep): { label: string; bg: string; color: string } {
  const key = getNodeTypeKey(node);
  if (!key) return { label: node.kind, bg: "surfaceLight30.main", color: "text50.main" };
  const meta = NODE_TYPES[key];
  return {
    label: meta.label,
    bg: `${meta.paletteToken}.main`,
    color: meta.textColor,
  };
}

export const NodeInfoPanel: FC<NodeInfoPanelProps> = ({ node, onClose, onReframe, scriptbookId }) => {
  const navigator = useNavigator();

  return (
    <Drawer
      anchor="right"
      open={!!node}
      variant="temporary"
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: 480,
          p: 3,
          boxSizing: "border-box",
          bgcolor: "surfaceLight20.main",
        },
      }}
    >
      {node && (
        <Stack spacing={2}>
          <Stack direction="row" sx={{ alignItems: "flex-start", justifyContent: "space-between" }}>
            <Typography
              variant="heading.seven"
              noWrap
              title={node.name}
              sx={{ pr: 1, overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}
            >
              {node.name}
            </Typography>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <XIcon size={20} />
            </IconButton>
          </Stack>
          {(() => {
            const badge = getNodeTypeBadge(node);
            return (
              <Chip
                label={badge.label}
                size="small"
                sx={{
                  alignSelf: "flex-start",
                  bgcolor: badge.bg,
                  color: badge.color,
                  fontWeight: 600,
                }}
              />
            );
          })()}
          <Stack spacing={1.5} divider={<Divider flexItem />}>
            {node.stepKind && <InfoRow label="Kind" value={node.stepKind} />}
            {node.isDestTable && <InfoRow label="Created by" value="download CREATE TABLE" />}
            {node.project && <InfoRow label="Project" value={node.project} />}
            {node.declarationName && node.declarationName !== node.name && (
              <InfoRow label="Declaration" value={node.declarationName} />
            )}
            {node.line > 0 && <InfoRow label="Line" value={String(node.line)} />}
          </Stack>
          <Button variant="contained" size="small" startIcon={<CrosshairIcon size={16} />} onClick={onReframe}>
            Graph from this perspective
          </Button>
          {scriptbookId && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowSquareOutIcon size={16} />}
              href={`#${navigator.route(`scripts/editor/${scriptbookId}`, {
                mode: "Files",
                variant: "editor",
                ...(node.project ? { file: encodeURIComponent(node.project) } : {}),
              })}`}
              // TODO: replace with a `color="info"` outlined Button variant in
              // sbComponents.ts (mirroring the existing primary/secondary variants)
              // so these per-state overrides don't have to live here.
              sx={{
                color: "accentInfo30.main",
                borderColor: "accentInfo30.main",
                boxShadow: "none",
                "&:hover": {
                  borderColor: "accentInfo40.main",
                  color: "accentInfo40.main",
                  backgroundColor: "accentInfo10.main",
                  boxShadow: "none",
                },
                "&:focus-visible": { boxShadow: "none" },
                "&:active": { boxShadow: "none" },
              }}
            >
              Go to SBS
            </Button>
          )}
        </Stack>
      )}
    </Drawer>
  );
};
